import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench } from 'lucide-react';
import { API_BASE } from '@/api/apiFetch';

interface MaintenancePayload {
  active: boolean;
  start?: string;
  end?: string;
  scope?: string;
}

const POLL_INTERVAL_MS = 60_000;
// Maintenance flag lives outside the auth-protected `/api` namespace so the
// banner can render on the login screen too (e.g. while ops repairs Cognito) —
// but it's still a verix-api route, not a same-origin one: sin API_BASE esto
// resolvía contra el propio dominio del frontend (la landing, con /app), que
// nunca sirvió esta ruta. El catch silencioso de abajo hacía que el fallo no
// se viera nunca — el banner simplemente no podía aparecer.
const MAINTENANCE_URL = API_BASE + '/health/maintenance';

/**
 * Top-of-screen banner driven by the public `/health/maintenance` endpoint.
 *
 * Renders nothing when no maintenance window is active. When the backend
 * returns `{ active: true, end: <ISO> }` the banner shows the localized
 * `errors.feature.scheduled` copy with the end timestamp interpolated, and
 * polls every minute so it disappears automatically as soon as ops clears
 * the flag — no page reload required.
 *
 * Failure mode: if the endpoint is unreachable we silently keep the banner
 * hidden. We never want this surface to itself become a source of noise.
 */
export function MaintenanceBanner() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<MaintenancePayload | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const res = await fetch(MAINTENANCE_URL, { credentials: 'omit' });
        if (!res.ok) return;
        const json = (await res.json()) as MaintenancePayload;
        if (!cancelled) setData(json);
      } catch {
        // Silent: we don't want a banner about a banner.
      }
    }

    fetchStatus();
    const id = window.setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!data?.active) return null;

  const eta = data.end ? formatDate(data.end, i18n.language) : undefined;
  const description = t('errors.feature.scheduled.description', { eta }) as unknown as string;
  const title = t('errors.feature.scheduled.title') as unknown as string;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-amber-50 text-amber-900 border-b border-amber-200 px-4 py-2 flex items-center gap-3 text-sm"
    >
      <Wrench className="h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="flex flex-wrap gap-x-2">
        <span className="font-medium">{title}</span>
        <span className="opacity-80">{description}</span>
      </div>
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale || 'es-ES', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
}

export default MaintenanceBanner;
